import i18n, { i18n as i18nType } from 'i18next'
import { initReactI18next } from 'react-i18next'
import Backend from 'i18next-http-backend'

declare global {
	interface Window {
		l10n?: { lang: string; defineType: 'path' | 'subdomain' }
		__localesVersion__?: number | null
	}
}

const localesVersion = window.__localesVersion__ || null

interface InitI18NArgs {
	instance?: i18nType
	ns?: string | string[]
	lang?: string
	initReact?: boolean
	defineType?: 'path' | 'subdomain'
}

const initI18n = ({
	instance: _instance,
	ns,
	lang,
	initReact,
	defineType,
}: InitI18NArgs) => {
	window.l10n = { lang, defineType: defineType || 'path' }
	const instance = _instance || i18n.createInstance()
	if (initReact) {
		instance.use(initReactI18next)
	}
	instance
		.use(Backend)
		// .use(LanguageDetector)
		.init({
			fallbackLng: lang,
			lng: lang,
			backend: {
				queryStringParams: localesVersion ? { v: localesVersion } : {},
			},
			// debug: true,
			// detection: {
			// 	order: ['queryString', 'cookie'],
			// 	lookupQuerystring: 'lng',
			// 	caches: ['cookie'],
			// },

			// load: 'currentOnly',
			partialBundledLanguages: true,
			ns,
			interpolation: { escapeValue: false },
		})

	return instance
}

export const SUPPORTED_LANGS: Record<
	string,
	{ russian: string; native: string; props?: { rtl?: boolean; beta?: boolean } }
> = {
	az: { russian: 'Азербайджанский', native: 'Azərbaycan' },
	be: { russian: 'Белорусский', native: 'Беларускі' },
	de: { russian: 'Немецкий', native: 'Deutsch' },
	el: { russian: 'Греческий', native: 'Ελληνικά' },
	en: { russian: 'Английский', native: 'English' },
	es: { russian: 'Испанский', native: 'Español' },
	fi: { russian: 'Финский', native: 'Suomalainen' },
	fr: { russian: 'Французский', native: 'Français' },
	hy: { russian: 'Армянский', native: 'հայերեն' },
	it: { russian: 'Итальянский', native: 'Italiano' },
	ja: { russian: 'Японский', native: '日本語' },
	ka: { russian: 'Грузинский', native: 'ქართული' },
	kk: { russian: 'Казахский', native: 'қазақ' },
	ko: { russian: 'Корейский', native: '한국어' },
	pl: { russian: 'Польский', native: 'Polski' },
	ru: { russian: 'Русский', native: 'Русский' },
	tr: { russian: 'Турецкий', native: 'Türkçe' },
	zh: { russian: 'Китайский', native: '中文' },
	ar: { russian: 'Арабский', native: 'عرب', props: { rtl: true, beta: true } },
	he: { russian: 'Иврит', native: 'עִברִית', props: { rtl: true, beta: true } },
}

const DATE_TEMPLATES: Record<
	string,
	{ template: string; regexp: RegExp[]; mask: string }
> = {
	ru: {
		template: 'DD.MM.YYYY',
		regexp: [
			/[0-9]{4}-(0[1-9]|1[0-2])-(0[1-9]|[1-2][0-9]|3[0-1])/,
			/([1-9]|([012][0-9])|(3[01]))\.([0]{0,1}[1-9]|1[012])\.[0-9]{4}/,
			/(3[01]|[12][0-9]|0?[1-9])\.(1[012]|0?[1-9])\.((?:19|20)\d{2})/,
		],
		mask: '99.99.9999',
	},
	en: {
		template: 'MM/DD/YYYY',
		regexp: [/(1[012]|0?[1-9])\/(3[01]|[12][0-9]|0?[1-9])\/((?:19|20)\d{2})/],
		mask: '99/99/9999',
	},
	es: {
		template: 'DD/MM/YYYY',
		regexp: [/(3[01]|[12][0-9]|0?[1-9])\/(1[012]|0?[1-9])\/((?:19|20)\d{2})/],
		mask: '99/99/9999',
	},
	zh: {
		template: 'YYYY/MM/DD',
		regexp: [/(3[01]|[12][0-9]|0?[1-9])\/(1[012]|0?[1-9])\/((?:19|20)\d{2})/],
		mask: '9999/99/99',
	},
	ko: {
		template: 'YYYY.MM.DD.',
		regexp: [/((?:19|20)\d{2})\.(1[012]|0?[1-9])\.(3[01]|[12][0-9]|0?[1-9])\./],
		mask: '9999.99.99.',
	},
	ar: {
		template: 'DD/MM/YYYY',
		regexp: [/(3[01]|[12][0-9]|0?[1-9])\/(1[012]|0?[1-9])\/((?:19|20)\d{2})/],
		mask: '99/99/9999',
	},
}

export const DATE_FORMATS: Record<
	string,
	{ mask: string; template: string; regexp: RegExp[] }
> = {
	az: DATE_TEMPLATES.ru,
	be: DATE_TEMPLATES.ru,
	el: DATE_TEMPLATES.es,
	es: DATE_TEMPLATES.es,
	it: DATE_TEMPLATES.es,
	ar: DATE_TEMPLATES.ar,
	en: DATE_TEMPLATES.en,
	he: DATE_TEMPLATES.es,
	ja: DATE_TEMPLATES.zh,
	fr: DATE_TEMPLATES.es,
	hy: DATE_TEMPLATES.ru,
	pl: DATE_TEMPLATES.ru,
	fi: DATE_TEMPLATES.ru,
	kk: DATE_TEMPLATES.ru,
	ko: DATE_TEMPLATES.ko,
	ru: DATE_TEMPLATES.ru,
	tr: DATE_TEMPLATES.ru,
	de: DATE_TEMPLATES.ru,
	zh: DATE_TEMPLATES.zh,
	ka: DATE_TEMPLATES.es,
}

export const MAPS_LANGS: Record<
	string,
	'tr_TR' | 'en_US' | 'en_RU' | 'ru_RU' | 'ru_UA' | 'uk_UA' | 'he_IL'
> = {
	ar: 'en_RU',
	az: 'en_RU',
	be: 'ru_RU',
	de: 'en_RU',
	el: 'en_RU',
	en: 'en_RU',
	es: 'en_RU',
	fi: 'en_RU',
	fr: 'en_RU',
	he: 'he_IL',
	hy: 'en_RU',
	it: 'en_RU',
	ja: 'en_RU',
	ka: 'en_RU',
	kk: 'ru_RU',
	ko: 'en_RU',
	pl: 'en_RU',
	ru: 'ru_RU',
	tr: 'tr_TR',
	zh: 'en_RU',
}

export const CAPTCHA_LANGS: Record<string, 'ru' | 'en' | 'be' | 'kk' | 'tr'> = {
	ar: 'en',
	az: 'en',
	be: 'be',
	de: 'en',
	el: 'en',
	en: 'en',
	es: 'en',
	fi: 'en',
	fr: 'en',
	he: 'en',
	hy: 'en',
	it: 'en',
	ja: 'en',
	ka: 'en',
	kk: 'kk',
	pl: 'en',
	ko: 'en',
	ru: 'ru',
	tr: 'tr',
	zh: 'en',
}

export { initI18n }
